import React, { useEffect, useState } from 'react';
import { Container, Card, CardBody, Form, Button, Alert } from 'reactstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import supplierService from '../../services/supplierService';
import SupplierArticles from './SupplierArticles';
import SupplierInfoFields, { supplierDisplayName } from './SupplierInfoFields';
import { FaArrowLeft, FaCheck, FaUser } from 'react-icons/fa';
import './SupplierForm.css';
import '../Reception/CompactPurchases.css';

const FournisseurForm = () => {
    const navigate = useNavigate();
    const params = useParams();

    // Fournisseur state
    const [fournisseur, setFournisseur] = useState({
        nom: '',
        prenom: '',
        cin: '',
        telephone1: '',
        telephone2: '',
        email: '',
        adresse: '',
        ville: 'Casablanca',
        typeFournisseur: 'PARTICULIER',
        codePostal: '',
        plafond: 0,
        entreprise: '', // New field for "Entreprise"
        ice: '',// New field for "ICE",
        active: true,
        solde: 0 // Ce champ sera en lecture seule
    });

    const location = useLocation();
    const closeForm = () => navigate('/purchases-module/fournisseurs');

    const fournisseurId = params.id; // For module routes: /fournisseurs/:id/edit
    const fournisseurEdit = location.state?.fournisseur || null; // For legacy routes

    const editId = fournisseurId || fournisseurEdit?.id;
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(Boolean(editId));
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [reload, setReload] = useState(0);

    useEffect(() => {
        if (!editId) return;
        let active = true;
        setLoading(true);
        setLoadError('');
        Promise.all([supplierService.get(editId), supplierService.articles(editId)])
            .then(([supplier, linkedArticles]) => {
                if (!active) return;
                setFournisseur(previous => ({ ...previous, ...supplier, active: supplier.active !== false }));
                setArticles(linkedArticles || []);
            })
            .catch(error => { if (active) setLoadError(error?.response?.data?.message || 'Chargement du fournisseur impossible.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [editId, reload]);

    const [errors, setErrors] = useState({});
    const handleSubmit = async event => {
        event.preventDefault();
        if (loading || saving || loadError) return;
        const validation = {};
        if (!supplierDisplayName(fournisseur).trim()) validation.name = 'Indiquez le nom du fournisseur.';
        setErrors(validation);
        if (Object.keys(validation).length) return;
        if (articles.some(article => article.prixAchatHT === '' || !Number.isFinite(Number(article.prixAchatHT)) || Number(article.prixAchatHT) < 0)) {
            setSaveError('Les prix des articles doivent être positifs ou nuls.');
            return;
        }
        setSaving(true);
        setSaveError('');
        const data = { ...fournisseur, articles: articles.map(article => ({ articleId: article.articleId, prixAchatHT: Number(article.prixAchatHT) })) };
        try {
            if (editId) await supplierService.update({ id: editId, data });
            else await supplierService.save(data);
            closeForm();
        } catch (error) {
            setSaveError(error?.response?.data?.message || 'Enregistrement du fournisseur impossible.');
        } finally { setSaving(false); }
    };

    return <Container fluid className="supplier-form-page compact-supplier-form py-2">
        <div className="supplier-form-shell">
            <Button type="button" color="link" className="supplier-back" disabled={saving} onClick={closeForm}><FaArrowLeft aria-hidden="true" /> Fournisseurs</Button>
            <header className="supplier-form-heading">
                <span className="supplier-form-icon"><FaUser aria-hidden="true" /></span>
                <div><h1>{editId ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}</h1><p>Ses coordonnées et les articles qu’il vous fournit.</p></div>
            </header>
            {loading && <p role="status">Chargement…</p>}
            {loadError && <Alert color="danger">{loadError} <Button type="button" color="link" onClick={() => setReload(value => value + 1)}>Réessayer</Button></Alert>}
            {saveError && <Alert color="danger">{saveError}</Alert>}
            <Form onSubmit={handleSubmit} aria-busy={saving || loading}>
                <fieldset disabled={loading || saving || Boolean(loadError)}>
                    <Card className="supplier-info-card"><CardBody>
                        <h2>Informations du fournisseur</h2>
                        <p className="supplier-form-hint">Seul le nom est obligatoire.</p>
                        <SupplierInfoFields supplier={fournisseur} onChange={setFournisseur} errors={errors} />
                    </CardBody></Card>
                    <SupplierArticles supplierId={editId} articles={articles} onChange={setArticles} disabled={loading || saving || Boolean(loadError)} />
                    <footer className="supplier-form-footer">
                        <span>{articles.length} article{articles.length > 1 ? 's' : ''} chez ce fournisseur</span>
                        <div><Button type="button" color="light" disabled={saving} onClick={closeForm}>Annuler</Button>
                            <Button type="submit" color="primary"><FaCheck aria-hidden="true" /> {saving ? 'Enregistrement…' : 'Enregistrer le fournisseur'}</Button></div>
                    </footer>
                </fieldset>
            </Form>
        </div>
    </Container>;
};

export default FournisseurForm;
